import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Formik, FormikErrors } from 'formik';
import { axiosKK } from '../../services/networkRequest';
import {
  LogisticsUserRole,
  PartnerUserAccess,
  UpdateLogisticsUserRequest,
} from '../../models/APIModels';
import errorMessage from '../../services/errorMessage';
import SubmitButton from '../SubmitButton';
import Label from '../Label';
import Checkbox from '../Checkbox';
import { CloseButton } from './Shared';

interface Props {
  show: boolean;
  user: PartnerUserAccess | null;
  close: () => void;
  onSuccess: (user: PartnerUserAccess) => void;
}

interface FormValues {
  email: string;
  name: string;
  role: LogisticsUserRole | '';
  phone: string;
  password: string;
  isActive: boolean;
  logisticsDashboardEnabled: boolean;
  canControlLogistics: boolean;
}

const ROLES: { value: LogisticsUserRole; label: string }[] = [
  { value: 'MASTER', label: 'Master' },
  { value: 'DRIVER', label: 'Driver' },
  { value: 'WAREHOUSE_STAFF', label: 'Warehouse Staff' },
];

function validate(values: FormValues): FormikErrors<FormValues> {
  const errors: FormikErrors<FormValues> = {};
  if (!values.email) errors.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
    errors.email = 'Invalid email';
  if (!values.name) errors.name = 'Name is required';
  if (!values.role) errors.role = 'Role is required';
  if (values.password && values.password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }
  return errors;
}

function isValidRole(role: string): role is LogisticsUserRole {
  return role === 'MASTER' || role === 'DRIVER' || role === 'WAREHOUSE_STAFF';
}

export default function EditLogisticsUserModal({
  show,
  user,
  close,
  onSuccess,
}: Props) {
  const [networkError, setNetworkError] = React.useState<string>();

  useEffect(() => {
    if (!show) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, close]);

  if (!show || !user) return null;

  const initialValues: FormValues = {
    email: user.email,
    name: user.name,
    role: isValidRole(user.role) ? user.role : '',
    phone: '',
    password: '',
    isActive: user.isActive,
    logisticsDashboardEnabled: user.logisticsDashboardEnabled,
    canControlLogistics: !!user.canControlLogistics,
  };

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div
        className="logistics-modal__container"
        onClick={(e) => e.stopPropagation()}
      >
        <Formik
          initialValues={initialValues}
          validate={validate}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            setNetworkError(undefined);
            // Send only fields that changed from the initial values, so an
            // empty phone/password input doesn't clobber existing data.
            const payload: UpdateLogisticsUserRequest = {};
            if (values.email !== initialValues.email) payload.email = values.email.trim();
            if (values.name !== initialValues.name) payload.name = values.name.trim();
            if (values.role !== initialValues.role && isValidRole(values.role)) {
              payload.role = values.role;
            }
            if (values.phone.trim()) payload.phone = values.phone.trim();
            if (values.password) payload.password = values.password;
            if (values.isActive !== initialValues.isActive) payload.isActive = values.isActive;
            if (values.logisticsDashboardEnabled !== initialValues.logisticsDashboardEnabled) {
              payload.logisticsDashboardEnabled = values.logisticsDashboardEnabled;
            }
            if (values.canControlLogistics !== initialValues.canControlLogistics) {
              payload.canControlLogistics = values.canControlLogistics;
            }

            axiosKK
              .put<PartnerUserAccess>(
                `/logistics/admin/partners/${user.partnerId}/users/${user.id}`,
                payload
              )
              .then(({ data }) => {
                onSuccess(data);
                close();
              })
              .catch((error) => {
                errorMessage(setNetworkError)(error);
              })
              .finally(() => setSubmitting(false));
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            dirty,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="logistics-modal__form"
              name="edit-logistics-user-form"
            >
              <div className="logistics-modal__header">
                <div className="logistics-modal__header-info">
                  <h3>Edit logistics user</h3>
                  <span className="logistics-modal__subtitle">
                    {user.partnerName} · #{user.id}
                  </span>
                </div>
                <CloseButton
                  onClick={close}
                  ariaLabel="Close edit user modal"
                  className="logistics-modal__close"
                />
              </div>

              <div className="logistics-modal__body">
                <div className="logistics-modal__section">
                  <div className="logistics-modal__field">
                    <Label htmlFor="edit-name">Name</Label>
                    <input
                      className="logistics-modal__input"
                      type="text"
                      id="edit-name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.name && errors.name && (
                      <span className="error">{errors.name}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="edit-email">Email</Label>
                    <input
                      className="logistics-modal__input"
                      type="email"
                      id="edit-email"
                      name="email"
                      autoComplete="off"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.email && errors.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="edit-role">Role</Label>
                    <select
                      className="logistics-modal__select"
                      id="edit-role"
                      name="role"
                      value={values.role}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value="">— Select role —</option>
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                    {touched.role && errors.role && (
                      <span className="error">{errors.role}</span>
                    )}
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="edit-phone">Phone (optional)</Label>
                    <input
                      className="logistics-modal__input"
                      type="tel"
                      id="edit-phone"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>

                  <div className="logistics-modal__field">
                    <Label htmlFor="edit-password">
                      New password (leave blank to keep)
                    </Label>
                    <input
                      className="logistics-modal__input"
                      type="password"
                      id="edit-password"
                      name="password"
                      autoComplete="new-password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.password && errors.password && (
                      <span className="error">{errors.password}</span>
                    )}
                  </div>
                </div>

                <div className="logistics-modal__section logistics-modal__section--checkbox-stack">
                  <Checkbox
                    name="isActive"
                    checked={values.isActive}
                    onChange={handleChange}
                    label="Active account"
                  />
                  <Checkbox
                    name="logisticsDashboardEnabled"
                    checked={values.logisticsDashboardEnabled}
                    onChange={handleChange}
                    label="Dashboard access enabled"
                  />
                  <Checkbox
                    name="canControlLogistics"
                    checked={values.canControlLogistics}
                    onChange={handleChange}
                    label="Can control logistics (split / reorder routes)"
                  />
                </div>

                {networkError && <span className="error">{networkError}</span>}
              </div>

              <div className="logistics-modal__footer">
                <SubmitButton
                  disabled={!isValid || !dirty}
                  isSubmitting={isSubmitting}
                >
                  Save changes
                </SubmitButton>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>,
    document.body
  );
}
